/**
 * Services tab component.
 *
 * Displays ECS service health for all configured microservices.
 *
 * Implementation plan reference: D2.16, D2.16a
 */

import { useEffect, useState } from 'react';
import { ApiError, getServices } from '@/api/client';
import { formatDateTime } from '@/utils/format';
import { POLL_INTERVAL_SERVICES_ALERTS_MS, useRefresh } from '@/context/RefreshContext';
import { Dot } from '@/components/Dot';
import { FloatingTooltip } from '@/components/FloatingTooltip';
import { MiniBar } from '@/components/MiniBar';
import type { EcsService, Environment, ServiceEnvironmentStatus, ServicesResponse } from '@/types/api';
import {
  SERVICES_CPU_PERCENT,
  SERVICES_MEMORY_PERCENT,
  SERVICES_THRESHOLD_TOOLTIPS,
  errorRate5xxPerMinSeverity,
  latencyMsSeverity,
  metricCellClassName,
  miniBarVariantForSeverity,
  percentUtilizationSeverity,
  tasksSeverity,
} from '@/utils/servicesMetricThresholds';

interface ServicesTabProps {
  environment: Environment;
}

function statusForEnv(service: EcsService, env: Environment): ServiceEnvironmentStatus | undefined {
  return service.environments.find((e) => e.environment === env);
}

function dotStatus(health: ServiceEnvironmentStatus['health']): 'healthy' | 'degraded' | 'down' | 'unknown' {
  if (health === 'healthy' || health === 'degraded' || health === 'down') {
    return health;
  }
  return 'unknown';
}

function formatCpuConfigured(vcpu: number): string {
  if (vcpu === 0.25) return '/ ¼ vCPU';
  if (vcpu === 0.5) return '/ ½ vCPU';
  return `/ ${vcpu} vCPU`;
}

function formatMemoryConfigured(mb: number): string {
  if (mb < 1024) return `/ ${mb} MB`;
  const gb = mb / 1024;
  return `/ ${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
}

function formatHigh24hPercent(value: number | undefined): string {
  if (value === undefined) {
    return '—';
  }
  return `${Math.round(value * 10) / 10}%`;
}

function formatHigh24hMs(value: number | undefined): string {
  if (value === undefined) {
    return '—';
  }
  return String(value);
}

function formatHigh24h5xx(value: number | undefined): string {
  if (value === undefined) {
    return '—';
  }
  return value.toFixed(1);
}

function configuredUnknownMessage(cell: ServiceEnvironmentStatus): string {
  const raw = cell.configError?.trim();
  const hasUsefulRaw =
    raw !== undefined && raw !== '' && raw !== '?' && raw.length >= 4;
  if (hasUsefulRaw) {
    return `Configured CPU/memory capacity is unknown: ${raw}`;
  }
  return 'Configured CPU/memory capacity is unknown (capacity metadata unavailable from ECS task definition).';
}

const SERVICES_COLUMN_TOOLTIP = {
  Service: 'Display name of the microservice in this dashboard.',
  Health:
    'Rollup from ECS running vs desired task counts and recent HTTP 5xx from the load balancer target group (healthy, degraded, or down).',
  Tasks: `Running tasks and desired tasks for this service in the selected environment (from ECS DescribeServices). ${SERVICES_THRESHOLD_TOOLTIPS.tasks}`,
  CPU: `Average CPU utilization as a percentage of the task configured vCPU. The suffix shows configured vCPU when known from the task definition. ${SERVICES_THRESHOLD_TOOLTIPS.cpu}`,
  Memory: `Average memory utilization versus configured task memory. The suffix shows configured memory when known from the task definition. ${SERVICES_THRESHOLD_TOOLTIPS.memory}`,
  'p95 (ms)': `95th percentile of target response time in milliseconds (CloudWatch ApplicationELB TargetResponseTime, p95, for this service load balancer and target group). ${SERVICES_THRESHOLD_TOOLTIPS.p95}`,
  '5xx / min': `Estimated HTTP 5xx responses per minute from registered targets (CloudWatch ApplicationELB HTTPCode_Target_5XX_Count over the recent metrics window). ${SERVICES_THRESHOLD_TOOLTIPS.error5xx}`,
  'CPU 24h':
    `Highest CPU utilization % in the last 24 hours (max of 5-minute CloudWatch buckets). Brief spikes are smoothed within each bucket. ${SERVICES_THRESHOLD_TOOLTIPS.cpu}`,
  'Mem 24h':
    `Highest memory utilization % in the last 24 hours (max of 5-minute buckets). ${SERVICES_THRESHOLD_TOOLTIPS.memory}`,
  'p95 24h':
    `Highest per-bucket p95 target response time (ms) in the last 24 hours (5-minute buckets). ${SERVICES_THRESHOLD_TOOLTIPS.p95}`,
  '5xx 24h':
    `Peak HTTP 5xx rate (errors per minute) in the last 24 hours (max of 5-minute bucket sums, normalized to per minute). ${SERVICES_THRESHOLD_TOOLTIPS.error5xx}`,
  Deployed:
    'When ECS last updated the PRIMARY deployment for this service (last rollout or task definition change from DescribeServices deployment updatedAt).',
} as const;

type ServicesColumnLabel = keyof typeof SERVICES_COLUMN_TOOLTIP;

function ConfiguredCapacityUnknownHint({ message }: { message: string }): JSX.Element {
  return (
    <FloatingTooltip
      tooltip={message}
      ariaLabel={message}
      wrapClassName="services-unknown-wrap"
      triggerClassName="services-unknown-indicator"
      placement="below"
    >
      ?
    </FloatingTooltip>
  );
}

function ServicesColumnHead({ label }: { label: ServicesColumnLabel }): JSX.Element {
  const tip = SERVICES_COLUMN_TOOLTIP[label];
  return (
    <th scope="col">
      <FloatingTooltip
        tooltip={tip}
        ariaLabel={`${label}: ${tip}`}
        triggerClassName="data-table-th-tooltip-trigger"
        placement="above"
      >
        {label}
      </FloatingTooltip>
    </th>
  );
}

export function ServicesTab({ environment }: ServicesTabProps): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible } = useRefresh();
  const [data, setData] = useState<ServicesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function runFetch(silent: boolean): void {
      if (!silent) {
        setIsLoading(true);
        setError(null);
        setData(null);
      }

      void getServices(environment)
        .then((response) => {
          if (!cancelled) {
            setData(response);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || silent) {
            return;
          }
          if (err instanceof ApiError) {
            setError(`Failed to load services data (${err.status}).`);
          } else {
            setError('Failed to load services data.');
          }
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    runFetch(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => runFetch(true), POLL_INTERVAL_SERVICES_ALERTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [environment, mode, tabVisible, manualRefreshNonce]);

  function formatWhen(iso: string | undefined): string {
    if (iso === undefined || iso === '') return '—';
    return formatDateTime(iso, { dateStyle: 'medium', timeStyle: 'short' });
  }

  return (
    <div className="services-tab">
      <h2>Services Health - {environment.toUpperCase()}</h2>
      <p className="tab-lead">ECS services and metrics for the selected environment.</p>

      {isLoading && <p className="placeholder">Loading services data...</p>}
      {!isLoading && error !== null && <p className="placeholder">{error}</p>}
      {!isLoading && error === null && data !== null && (
        <>
          <p className="fetched-at">Last updated {formatWhen(data.fetchedAt)}</p>
          <div className="table-wrap">
            <table className="data-table services-table">
              <thead>
                <tr>
                  <ServicesColumnHead label="Service" />
                  <ServicesColumnHead label="Health" />
                  <ServicesColumnHead label="Tasks" />
                  <ServicesColumnHead label="CPU" />
                  <ServicesColumnHead label="CPU 24h" />
                  <ServicesColumnHead label="Memory" />
                  <ServicesColumnHead label="Mem 24h" />
                  <ServicesColumnHead label="p95 (ms)" />
                  <ServicesColumnHead label="p95 24h" />
                  <ServicesColumnHead label="5xx / min" />
                  <ServicesColumnHead label="5xx 24h" />
                  <ServicesColumnHead label="Deployed" />
                </tr>
              </thead>
              <tbody>
                {data.services.map((svc) => {
                  const cell = statusForEnv(svc, environment);
                  if (cell === undefined) {
                    const envs = svc.environments.map((e) => e.environment).join(', ');
                    return (
                      <tr key={svc.serviceName}>
                        <td colSpan={12} className="table-empty-row">
                          No metrics for {svc.displayName} in {environment}
                          {envs !== '' ? ` (API returned: ${envs})` : ''}
                        </td>
                      </tr>
                    );
                  }
                  const cpuSeverity = percentUtilizationSeverity(
                    cell.cpuPercent,
                    SERVICES_CPU_PERCENT.warnAt,
                    SERVICES_CPU_PERCENT.errorAt,
                  );
                  const cpuHighSeverity = percentUtilizationSeverity(
                    cell.cpuPercentHigh24h,
                    SERVICES_CPU_PERCENT.warnAt,
                    SERVICES_CPU_PERCENT.errorAt,
                  );
                  const memSeverity = percentUtilizationSeverity(
                    cell.memoryPercent,
                    SERVICES_MEMORY_PERCENT.warnAt,
                    SERVICES_MEMORY_PERCENT.errorAt,
                  );
                  const memHighSeverity = percentUtilizationSeverity(
                    cell.memoryPercentHigh24h,
                    SERVICES_MEMORY_PERCENT.warnAt,
                    SERVICES_MEMORY_PERCENT.errorAt,
                  );
                  const p95Severity = latencyMsSeverity(cell.p95LatencyMs);
                  const p95HighSeverity = latencyMsSeverity(cell.p95LatencyMsHigh24h);
                  const error5xxSeverity = errorRate5xxPerMinSeverity(cell.errorRate5xx);
                  const error5xxHighSeverity = errorRate5xxPerMinSeverity(cell.errorRate5xxPerMinHigh24h);
                  const tasksCellSeverity = tasksSeverity(cell.runningCount, cell.desiredCount);

                  return (
                    <tr key={svc.serviceName}>
                      <td className="td-strong">{svc.displayName}</td>
                      <td>
                        <span className="cell-dot">
                          <Dot status={dotStatus(cell.health)} />
                          <span className="sr-only">{cell.health}</span>
                        </span>
                      </td>
                      <td className={metricCellClassName(tasksCellSeverity)}>
                        {cell.runningCount}/{cell.desiredCount}
                      </td>
                      <td className="td-mini">
                        <MiniBar
                          value={Math.round(cell.cpuPercent * 10) / 10}
                          label="CPU"
                          variant={miniBarVariantForSeverity(cpuSeverity)}
                          suffix={
                            cell.cpuConfigured !== undefined
                              ? formatCpuConfigured(cell.cpuConfigured)
                              : '/ —'
                          }
                        />
                        {cell.cpuConfigured === undefined && (
                          <ConfiguredCapacityUnknownHint message={configuredUnknownMessage(cell)} />
                        )}
                      </td>
                      <td className={metricCellClassName(cpuHighSeverity, true)}>
                        {formatHigh24hPercent(cell.cpuPercentHigh24h)}
                      </td>
                      <td className="td-mini">
                        <MiniBar
                          value={Math.round(cell.memoryPercent * 10) / 10}
                          label="Mem"
                          variant={miniBarVariantForSeverity(memSeverity)}
                          suffix={
                            cell.memoryConfiguredMB !== undefined
                              ? formatMemoryConfigured(cell.memoryConfiguredMB)
                              : '/ —'
                          }
                        />
                        {cell.memoryConfiguredMB === undefined && (
                          <ConfiguredCapacityUnknownHint message={configuredUnknownMessage(cell)} />
                        )}
                      </td>
                      <td className={metricCellClassName(memHighSeverity, true)}>
                        {formatHigh24hPercent(cell.memoryPercentHigh24h)}
                      </td>
                      <td className={metricCellClassName(p95Severity)}>{cell.p95LatencyMs}</td>
                      <td className={metricCellClassName(p95HighSeverity, true)}>
                        {formatHigh24hMs(cell.p95LatencyMsHigh24h)}
                      </td>
                      <td className={metricCellClassName(error5xxSeverity)}>
                        {cell.errorRate5xx.toFixed(1)}
                      </td>
                      <td className={metricCellClassName(error5xxHighSeverity, true)}>
                        {formatHigh24h5xx(cell.errorRate5xxPerMinHigh24h)}
                      </td>
                      <td className="td-muted">{formatWhen(cell.lastDeployedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
